import type { ReactNode } from "react";
import React, { createContext, useContext, useRef } from "react";

interface FeatureItem {
  index: number;
  evaluable?: boolean;
}

export interface FeaturesResult {
  data?: Map<string, FeatureItem> | null;
  status: "idle" | "error" | "success" | "loading";
}

interface FeatureContextType extends FeaturesResult {
  context: string;
  evaluatedFeatures: React.MutableRefObject<Set<string>>;
}

const IDLE_STATE: FeatureContextType = {
  status: "idle",
  context: "",
  evaluatedFeatures: { current: new Set([]) },
};

const FeaturesQueryContext = createContext<FeatureContextType>(IDLE_STATE);

export default function FeaturesProvider({
  children,
  featuresResult,
  context,
}: {
  featuresResult: FeaturesResult;
  children: ReactNode;
  context: string;
}) {
  const { data, status } = featuresResult || {};
  const evaluatedFeatures = useRef(new Set([]));

  return (
    <FeaturesQueryContext.Provider
      value={{ data, status, context, evaluatedFeatures }}
    >
      {children}
    </FeaturesQueryContext.Provider>
  );
}

export const useFeatureState = (name: string) => {
  const { status, data, context, evaluatedFeatures } =
    useContext(FeaturesQueryContext);
  const featureState = data?.get?.(name);
  return {
    status,
    context,
    evaluatedFeatures,
    ...featureState,
  };
};
